import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from '@/context/LanguageContext';
import { AuthProvider } from '@/context/AuthContext';
import { ServicesProvider } from '@/context/ServicesContext';
import { OfferProvider } from '@/context/OfferContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import AIAssistantWidget from '@/components/AIAssistantWidget';
import Home from '@/pages/Home';
import ServicePage from '@/pages/ServicePage';
import RepairPage from '@/pages/RepairPage';
import Contact from '@/pages/Contact';
import About from '@/pages/About';
import Login from '@/pages/Login';
import Portal from '@/pages/Portal';
import PlatformIndex from '@/pages/dashboards/PlatformIndex';
import ProxmoxDashboard from '@/pages/dashboards/ProxmoxDashboard';
import MonitoringDashboard from '@/pages/dashboards/MonitoringDashboard';
import SecurityDashboard from '@/pages/dashboards/SecurityDashboard';
import Admin from '@/pages/Admin';
import StatusPage from '@/pages/StatusPage';
import { LiveDataProvider } from '@/lib/live';
import { BootSequence, CursorGlow, RouteScan, ScrollProgress } from '@/components/experience/Experience';
import CommandPalette from '@/components/CommandPalette';
import MatrixMode from '@/components/wow/MatrixMode';

function RequireAdmin({ children }) {
  const hasToken = typeof window !== 'undefined' && localStorage.getItem('itw_admin_token');
  if (!hasToken) return <Navigate to="/admin" replace />;
  return children;
}

function Layout() {
  const location = useLocation();
  const bareRoutes = ['/login', '/register'];
  const isBare = bareRoutes.includes(location.pathname)
    || location.pathname.startsWith('/plattform')
    || location.pathname.startsWith('/admin');
  return (
    <>
      <ScrollProgress />
      <RouteScan />
      {!isBare && <Header />}
      <main className="relative">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/leistungen/geraete-reparatur" element={<RepairPage />} />
          <Route path="/leistungen/:slug" element={<ServicePage />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/ueber-uns" element={<About />} />
          <Route path="/login" element={<Login mode="login" />} />
          <Route path="/register" element={<Login mode="register" />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/plattform" element={<RequireAdmin><PlatformIndex /></RequireAdmin>} />
          <Route path="/plattform/proxmox" element={<RequireAdmin><ProxmoxDashboard /></RequireAdmin>} />
          <Route path="/plattform/monitoring" element={<RequireAdmin><MonitoringDashboard /></RequireAdmin>} />
          <Route path="/plattform/security" element={<RequireAdmin><SecurityDashboard /></RequireAdmin>} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isBare && <Footer />}
      {!isBare && <AIAssistantWidget />}
      <CommandPalette />
      <MatrixMode />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <ServicesProvider>
            <BrowserRouter>
              <LiveDataProvider>
                <OfferProvider>
                  <BootSequence />
                  <CursorGlow />
                  <Layout />
                </OfferProvider>
              </LiveDataProvider>
            </BrowserRouter>
          </ServicesProvider>
        </AuthProvider>
      </LanguageProvider>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
